import React, { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function NavBar() {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const languageDropdownRef = useRef(null);
  const navRef = useRef(null);
  const { t, i18n } = useTranslation();

  const handleNavClick = (sectionId) => {
    if (location.pathname !== "/") {
      window.location.href = `/#${sectionId}`;
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsLanguageDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
      if (languageDropdownRef.current && !languageDropdownRef.current.contains(event.target)) {
        setIsLanguageDropdownOpen(false);
      }
      if (isMenuOpen && navRef.current && !navRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <header className="bg-[#F5EFE6] shadow-md fixed w-full z-50">
      <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
        <div className="flex items-center space-x-8">
          <Link to="/" onClick={() => {
            setIsMenuOpen(false);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#4A4A4A] tracking-wide cursor-pointer">
              {t('title')}
            </h1>
          </Link>
        </div>

        <div className="flex items-center justify-end space-x-10 flex-1">
          <nav className="hidden lg:flex space-x-10 items-center">
            <button onClick={() => handleNavClick("about")} className="text-[#4A4A4A] text-lg font-medium hover:text-black transition duration-300">
              {t('about')}
            </button>
            <button onClick={() => handleNavClick("services")} className="text-[#4A4A4A] text-lg font-medium hover:text-black transition duration-300">
              {t('services')}
            </button>
            <Link to="/faq" className="text-[#4A4A4A] text-lg font-medium hover:text-black transition duration-300">
              {t('faqs')}
            </Link>
            <Link to="/menu" className="text-[#4A4A4A] text-lg font-medium hover:text-black transition duration-300">
              {t('menu')}
            </Link>
            <a href="sms:+16468203551">
              <button className="bg-gradient-to-r from-[#D8B4A0] to-[#B19787] text-white px-8 py-2 rounded-full shadow-md hover:from-[#CFA694] hover:to-[#A88D7F] transition duration-300 text-lg">
                {t('bookSession')}
              </button>
            </a>
          </nav>

          {/* Instagram and Language Dropdown */}
          <div className="flex items-center space-x-4">
            <div className="relative flex items-center" ref={dropdownRef}>
              <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="flex items-center space-x-2">
                <img src={require("./images/instagram.png")} alt="Instagram" className="w-6 h-6 hover:opacity-75 transition duration-300" />
              </button>
              {isDropdownOpen && (
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-2 bg-[#FDF8F3] shadow-lg rounded-md py-2 w-40 text-center">
                  <a href="https://www.instagram.com/jeye_lash" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-[#4A4A4A] hover:bg-gray-100">
                    @jeye_lash
                  </a>
                  <a href="https://www.instagram.com/sara_lash_lash/" target="_blank" rel="noopener noreferrer" className="block px-4 py-2 text-[#4A4A4A] hover:bg-gray-100">
                    @sara_lash_lash
                  </a>
                </div>
              )}
            </div>

            <div className="relative flex items-center" ref={languageDropdownRef}>
              <button onClick={() => setIsLanguageDropdownOpen(!isLanguageDropdownOpen)} className="flex items-center space-x-1 text-[#4A4A4A] text-sm font-medium hover:text-black transition duration-300">
                <Globe className="w-4 h-4" />
                <span>{i18n.language === 'en' ? 'EN' : 'KR'}</span>
              </button>
              {isLanguageDropdownOpen && (
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-2 bg-[#FDF8F3] shadow-lg rounded-md py-2 w-28 text-center">
                  <button onClick={() => changeLanguage('en')} className="block w-full px-3 py-1 text-[#4A4A4A] hover:bg-gray-100">
                    English
                  </button>
                  <button onClick={() => changeLanguage('ko')} className="block w-full px-3 py-1 text-[#4A4A4A] hover:bg-gray-100">
                    한국어
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Hamburger Menu */}
          <div className="lg:hidden flex items-center">
            <button onClick={() => setIsMenuOpen(!isMenuOpen)}>
              {isMenuOpen ? <X className="w-6 h-6 text-[#4A4A4A]" /> : <Menu className="w-6 h-6 text-[#4A4A4A]" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <nav ref={navRef} className={`${isMenuOpen ? 'flex' : 'hidden'} lg:hidden flex-col justify-center items-center bg-[#F5EFE6] shadow-md p-4 space-y-4 text-center`}>
        <button onClick={() => handleNavClick("about")} className="text-[#4A4A4A] text-lg font-medium hover:text-black transition duration-300">
          {t('about')}
        </button>
        <button onClick={() => handleNavClick("services")} className="text-[#4A4A4A] text-lg font-medium hover:text-black transition duration-300">
          {t('services')}
        </button>
        <Link to="/faq" onClick={() => setIsMenuOpen(false)} className="text-[#4A4A4A] text-lg font-medium hover:text-black transition duration-300">
          {t('faqs')}
        </Link>
        <Link to="/menu" onClick={() => setIsMenuOpen(false)} className="text-[#4A4A4A] text-lg font-medium hover:text-black transition duration-300">
          {t('menu')}
        </Link>
        <a href="sms:+16468203551" onClick={() => setIsMenuOpen(false)}>
          <button className="bg-gradient-to-r from-[#D8B4A0] to-[#B19787] text-white px-8 py-2 rounded-full shadow-md hover:from-[#CFA694] hover:to-[#A88D7F] transition duration-300 text-lg">
            {t('bookSession')}
          </button>
        </a>
      </nav>
    </header>
  );
}
